import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { NameDto } from '@model/NameDto'
import { PublicDomainDto } from '@model/PublicDomainDto'

import { getDomainName } from './cookie.utils'

export enum TickmillCompaniesEnum {
  GENERIC = 0,
  TICKMILL_EU = 1,
  TICKMILL_UK = 2,
  TICKMILL_SC = 3,
  TICKMILL_PA = 4,
  TICKMILL_AS = 5,
  TICKMILL_UAE = 6,
}

export type TickmillCompany = NameDto<TickmillCompaniesEnum>
export const LOCALE_MAP: Record<string, string> = {
  zh: 'cn',
  ja: 'en',
}

export const getTickmillSupportEmail = (
  entity: TickmillCompaniesEnum,
  productType = true
): string => {
  if (entity === TickmillCompaniesEnum.TICKMILL_EU) {
    return 'support@tickmill.eu'
  } else if (entity === TickmillCompaniesEnum.TICKMILL_UK) {
    return productType ? 'support@tickmill.co.uk' : 'clientservices@tickmill.co.uk'
  } else {
    return 'support@tickmill.com'
  }
}

export const getTickmillSupportPhone = (entity: TickmillCompaniesEnum): string => {
  return entity === TickmillCompaniesEnum.TICKMILL_EU
    ? '+357 25041710'
    : entity === TickmillCompaniesEnum.TICKMILL_UK
    ? '+44 203 871 3299'
    : '+852 5808 7849'
}

export const getContactUsLink = (
  domains?: PublicDomainDto,
  locale?: string,
  section?: string
): string => {
  const supportedLocale = locale ? LOCALE_MAP[locale] ?? locale : 'en'

  const homePageUrl = domains?.homePageUrl ?? ''
  const localeSlug = `${supportedLocale}/`
  const sectionSlug = section ? `#${section}` : ''

  return `${homePageUrl}/${localeSlug}about/contact-us${sectionSlug}`.replace(
    /(https?:\/\/)|(\/)+/g,
    (match, protocol) => protocol || '/'
  )
}

export const entityPerTickmillCompany: Record<TickmillCompaniesEnum, string> = {
  [TickmillCompaniesEnum.TICKMILL_EU]: 'eu',
  [TickmillCompaniesEnum.TICKMILL_SC]: 'sc',
  [TickmillCompaniesEnum.TICKMILL_UK]: 'uk',
  [TickmillCompaniesEnum.TICKMILL_AS]: 'as',
  [TickmillCompaniesEnum.TICKMILL_UAE]: 'uae',
  [TickmillCompaniesEnum.TICKMILL_PA]: 'partners',
  [TickmillCompaniesEnum.GENERIC]: 'partners',
}

export function getRedirectDomain(accountEntity: TickmillCompaniesEnum): string {
  if (
    window.env?.APP_ENTITY_DEFAULT_DOMAINS &&
    Object.keys(window.env.APP_ENTITY_DEFAULT_DOMAINS).length
  ) {
    const entityMap = Object.values(window.env.APP_ENTITY_DEFAULT_DOMAINS)
    const entityMapKey = entityMap.indexOf(entityPerTickmillCompany[accountEntity])
    if (entityMapKey >= 0) {
      return Object.keys(window.env.APP_ENTITY_DEFAULT_DOMAINS)[entityMapKey]
    }
    return window.location.host
  }
  return window.location.host
}

export const getTickmillCompanyByHostname = (
  skipCheckingPathName?: boolean
): TickmillCompaniesEnum => {
  const hostName = window.location.hostname.split('.')
  const pathName = window.location.pathname
  const isLoginPage = skipCheckingPathName
    ? false
    : pathName.includes('sign-up') ||
      pathName.includes('forgot-password') ||
      pathName.includes('login') ||
      pathName === '/'

  if (import.meta.env.DEV) {
    const reactAppEntity = import.meta.env.REACT_APP_ENTITY?.trim()

    if (reactAppEntity === 'UK') return TickmillCompaniesEnum.TICKMILL_UK
    if (reactAppEntity === 'SC') return TickmillCompaniesEnum.TICKMILL_SC
    if (reactAppEntity === 'AS') return TickmillCompaniesEnum.TICKMILL_AS
    if (reactAppEntity === 'EU') return TickmillCompaniesEnum.TICKMILL_EU
    if (reactAppEntity === 'PARTNERS') return TickmillCompaniesEnum.TICKMILL_PA
    if (reactAppEntity === 'GENERIC') return TickmillCompaniesEnum.GENERIC

    return TickmillCompaniesEnum.TICKMILL_SC
  }

  if (window.env?.APP_ENTITY?.length) {
    if (window.env.APP_CONSOLIDATED_DOMAIN) {
      return TickmillCompaniesEnum.GENERIC
    } else if (window.env.APP_ENTITY[0].includes('uk')) {
      return TickmillCompaniesEnum.TICKMILL_UK
    } else if (window.env.APP_ENTITY[0].includes('eu')) {
      return TickmillCompaniesEnum.TICKMILL_EU
    } else if (window.env.APP_ENTITY[0].includes('as')) {
      return isLoginPage ? TickmillCompaniesEnum.GENERIC : TickmillCompaniesEnum.TICKMILL_AS
    } else if (window.env.APP_ENTITY[0].includes('sc')) {
      return isLoginPage ? TickmillCompaniesEnum.GENERIC : TickmillCompaniesEnum.TICKMILL_SC
    } else {
      return TickmillCompaniesEnum.TICKMILL_PA
    }
  }

  if (getDomainName(true) === 'secure.tickmill.eu') return TickmillCompaniesEnum.TICKMILL_EU
  if (getDomainName(true) === 'secure.tickmill.co.uk') return TickmillCompaniesEnum.TICKMILL_UK

  if (hostName[0].includes('eu')) return TickmillCompaniesEnum.TICKMILL_EU
  if (hostName[0].includes('sc')) {
    return isLoginPage ? TickmillCompaniesEnum.GENERIC : TickmillCompaniesEnum.TICKMILL_SC
  }
  if (hostName[0].includes('uk')) return TickmillCompaniesEnum.TICKMILL_UK
  if (hostName[0].includes('partners')) return TickmillCompaniesEnum.TICKMILL_PA
  if (hostName[0].includes('as')) {
    return isLoginPage ? TickmillCompaniesEnum.GENERIC : TickmillCompaniesEnum.TICKMILL_AS
  }

  return TickmillCompaniesEnum.TICKMILL_UK
}

export const getTickmillPublicDomain = () => {
  const hostName = getDomainName(true).split('.')
  return hostName[0] === 'localhost' ? 'clientarea' : getDomainName(true)
}

export const getCompanyName = (id: TickmillCompaniesEnum | undefined) => {
  switch (id) {
    case TickmillCompaniesEnum.TICKMILL_EU:
      return 'TM EU'
    case TickmillCompaniesEnum.TICKMILL_UK:
      return 'TM UK'
    case TickmillCompaniesEnum.TICKMILL_SC:
      return 'TM SC'
    case TickmillCompaniesEnum.TICKMILL_PA:
      return 'TM Partners'
    case TickmillCompaniesEnum.TICKMILL_AS:
      return 'TM AS'
    default:
      return 'TM SC'
  }
}

export const getCompanyFullName = (id: TickmillCompaniesEnum | undefined): string => {
  switch (id) {
    case TickmillCompaniesEnum.TICKMILL_EU:
      return 'Tickmill EU Company'
    case TickmillCompaniesEnum.TICKMILL_UK:
      return 'Tickmill UK Company'
    case TickmillCompaniesEnum.TICKMILL_SC:
      return 'Tickmill SC Company'
    case TickmillCompaniesEnum.TICKMILL_PA:
      return 'Tickmill Partners Company'
    case TickmillCompaniesEnum.TICKMILL_AS:
      return 'Tickmill AS Company'
    default:
      return ''
  }
}

export enum TickmillCompaniesSuffixEnum {
  TICKMILL_EU = 'EU',
  TICKMILL_UK = 'UK',
  TICKMILL_SC = 'SC',
  TICKMILL_PA = 'PA',
  TICKMILL_AS = 'AS',
}

export const isTickmillGenericType = (type: TickmillCompaniesEnum | undefined): boolean => {
  const companyType = type?.toString()
  return companyType ? Number.parseInt(companyType) === TickmillCompaniesEnum.GENERIC : false
}

export const isEitherASTypeOrSCType = (type: TickmillCompaniesEnum | undefined): boolean =>
  isTickmillASType(type) || isTickmillSCType(type)

export const isTickmillEU = (account: AccountDetailedDto | undefined): boolean => {
  if (!account?.tickmillCompany) {
    return false
  }
  return account.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_EU
}

export const isTickmillEUType = (type: TickmillCompaniesEnum | undefined) => {
  if (!type) {
    return false
  }
  return type === TickmillCompaniesEnum.TICKMILL_EU
}

export const isTickmillSC = (account: AccountDetailedDto | undefined) => {
  if (!account?.tickmillCompany) {
    return false
  }
  return account.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_SC
}

export const isTickmillUAE = (account: AccountDetailedDto | undefined) => {
  if (!account?.tickmillCompany) {
    return false
  }
  return account.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_UAE
}

export const isTickmillSCType = (type: TickmillCompaniesEnum | undefined) => {
  if (!type) {
    return false
  }
  return type === TickmillCompaniesEnum.TICKMILL_SC
}

export const isTickmillUK = (account: AccountDetailedDto | undefined) => {
  if (!account?.tickmillCompany) {
    return false
  }
  return account.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_UK
}

export const isTickmillUKType = (type: TickmillCompaniesEnum | undefined) => {
  if (!type) {
    return false
  }
  return type === TickmillCompaniesEnum.TICKMILL_UK
}

export const isTickmillAS = (account: AccountDetailedDto | undefined) => {
  if (!account?.tickmillCompany) {
    return false
  }
  return account.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_AS
}

export const isTickmillASType = (type: TickmillCompaniesEnum | undefined) => {
  if (!type) {
    return false
  }
  return type === TickmillCompaniesEnum.TICKMILL_AS
}

export const isTickmillPartner = (account: AccountDetailedDto | undefined) => {
  if (!account?.tickmillCompany) {
    return false
  }
  return account.tickmillCompany.id === TickmillCompaniesEnum.TICKMILL_PA
}

export const isTickmillPartnerType = (type: TickmillCompaniesEnum | undefined) => {
  if (!type) {
    return false
  }
  return type === TickmillCompaniesEnum.TICKMILL_PA
}

export const isTickmillUAEType = (type: TickmillCompaniesEnum | undefined) => {
  if (!type) {
    return false
  }
  return type === TickmillCompaniesEnum.TICKMILL_UAE
}

export const requiresAppTestImprovement = (company: TickmillCompaniesEnum): boolean => {
  return (
    company === TickmillCompaniesEnum.TICKMILL_EU || company === TickmillCompaniesEnum.TICKMILL_UK
  )
}
